const SETTINGS_KEY = 'dnd_delivery_fee_simulator_settings';

export const DEFAULT_FEE_SETTINGS = {
  baseFee: '75',
  perMileRate: '3.50',
  perVehiclePrice: '0',
};

export function loadFeeSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_FEE_SETTINGS };
    const parsed = JSON.parse(raw);
    return {
      baseFee: parsed.baseFee ?? DEFAULT_FEE_SETTINGS.baseFee,
      perMileRate: parsed.perMileRate ?? DEFAULT_FEE_SETTINGS.perMileRate,
      perVehiclePrice: parsed.perVehiclePrice ?? DEFAULT_FEE_SETTINGS.perVehiclePrice,
    };
  } catch {
    return { ...DEFAULT_FEE_SETTINGS };
  }
}

export function saveFeeSettings(settings) {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

export function formatFullAddress(parts) {
  return [
    parts.delivery_address || parts.address,
    parts.delivery_city || parts.city,
    parts.delivery_state || parts.state,
    parts.delivery_zip || parts.zip_code,
  ]
    .filter(Boolean)
    .join(', ');
}

export function geocodeAddress(address) {
  return new Promise((resolve, reject) => {
    if (!window.google?.maps?.Geocoder) {
      reject(new Error('Google Maps is not loaded.'));
      return;
    }
    const trimmed = (address || '').trim();
    if (!trimmed) {
      reject(new Error('Address is required.'));
      return;
    }
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: trimmed }, (results, status) => {
      if (status === 'OK' && results?.[0]?.geometry?.location) {
        const loc = results[0].geometry.location;
        resolve({
          lat: loc.lat(),
          lng: loc.lng(),
          formatted: results[0].formatted_address,
        });
      } else {
        reject(new Error(`Could not find that address (${status}).`));
      }
    });
  });
}

export function fetchDrivingDistanceMiles(origin, destination) {
  return new Promise((resolve, reject) => {
    if (!window.google?.maps?.DistanceMatrixService) {
      reject(new Error('Google Distance Matrix is not available.'));
      return;
    }
    const service = new window.google.maps.DistanceMatrixService();
    service.getDistanceMatrix(
      {
        origins: [{ lat: origin.lat, lng: origin.lng }],
        destinations: [{ lat: destination.lat, lng: destination.lng }],
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.IMPERIAL,
      },
      (response, status) => {
        const element = response?.rows?.[0]?.elements?.[0];
        if (status === 'OK' && element?.status === 'OK' && element.distance?.value != null) {
          resolve({
            miles: element.distance.value / 1609.344,
            milesText: element.distance.text,
            durationText: element.duration?.text || null,
          });
        } else {
          reject(new Error('Could not calculate driving distance between home and delivery.'));
        }
      }
    );
  });
}

/**
 * Delivery fee = (base + distance_miles × per_mile + price_per_vehicle) × vehicle_count
 */
export function calculateDeliveryFee({
  distanceMiles,
  vehicleCount,
  baseFee,
  perMileRate,
  perVehiclePrice,
}) {
  const vehicles = Math.max(1, Number.parseInt(vehicleCount, 10) || 1);
  const base = Number.parseFloat(baseFee) || 0;
  const rate = Number.parseFloat(perMileRate) || 0;
  const vehiclePrice = Number.parseFloat(perVehiclePrice) || 0;
  const miles = Math.max(0, Number.parseFloat(distanceMiles) || 0);
  const distanceComponent = miles * rate;
  const perVehicle = base + distanceComponent + vehiclePrice;
  const total = perVehicle * vehicles;
  return {
    vehicleCount: vehicles,
    distanceMiles: miles,
    baseFee: base,
    perMileRate: rate,
    perVehiclePrice: vehiclePrice,
    distanceComponent,
    perVehicleFee: perVehicle,
    totalFee: Math.round(total * 100) / 100,
  };
}

export async function simulateDeliveryFee({
  homeAddress,
  deliveryAddress,
  vehicleCount,
  baseFee,
  perMileRate,
  perVehiclePrice,
}) {
  const [home, delivery] = await Promise.all([
    geocodeAddress(homeAddress),
    geocodeAddress(deliveryAddress),
  ]);
  const driving = await fetchDrivingDistanceMiles(home, delivery);
  const fee = calculateDeliveryFee({
    distanceMiles: driving.miles,
    vehicleCount,
    baseFee,
    perMileRate,
    perVehiclePrice,
  });
  return {
    home,
    delivery,
    driving,
    fee,
  };
}
